import { map } from 'es-toolkit/compat';
import type { FC } from 'react';
import { Dropdown } from 'react-bootstrap';
import { classes } from 'typestyle';
import { assertUnreachable } from '../../../app.util';
import type { CampaignMenuItem, PodBadge } from '../campaign.types';
import { CampaignBadges } from './CampaignBadges';
import { SaveButton } from './SaveButton';
import { SidebarButton } from './SidebarButton';
import { SidebarContainer } from './SidebarContainer';
import { SidebarDropdown } from './SidebarDropdown';
import { SidebarMenu } from './SidebarMenu';

export type CampaignSidebarLayoutProps = {
  badges: PodBadge[];
  menu: CampaignMenuItem[];
  isPublished: boolean;
  isLoading: boolean;
  onMenuItemClicked: (menuItem: CampaignMenuItem) => void;
  isMVT?: boolean;
};

function getCommonProps(menuItem: CampaignMenuItem) {
  return {
    disabled: menuItem.disabled,
    key: menuItem.id,
  };
}

export const CampaignSidebarLayout: FC<CampaignSidebarLayoutProps> = ({ badges, menu, onMenuItemClicked, isPublished, isLoading }) => {
  function menuItemClicked(menuItem: CampaignMenuItem) {
    onMenuItemClicked(menuItem);
  }

  const topItems = menu.filter((item) => !item.overflow);
  const visibleTopItems = topItems.filter((item) => item.visible);
  const overflowItems = menu.filter((item) => Boolean(item.overflow));
  const visibleOverflowItems = overflowItems.filter((item) => item.visible);

  return (
    <SidebarContainer isLoading={isLoading}>
      <CampaignBadges badges={badges} className="mb-2" />

      <SidebarMenu>
        {map(visibleTopItems, (menuItem) => {
          const commonProps = getCommonProps(menuItem);

          switch (menuItem.component) {
            case 'sidebar-dropdown': {
              const componentProps = menuItem.componentProps;
              return <SidebarDropdown {...commonProps} {...componentProps} />;
            }
            case 'sidebar-button':
              return (
                <SidebarButton
                  {...commonProps}
                  className={classes(menuItem.cssClass === undefined ? 'btn btn-sidebar' : menuItem.cssClass)}
                  data-cy={menuItem.id}
                  icon={menuItem.icon}
                  label={menuItem.label}
                  onClick={() => menuItemClicked(menuItem)}
                  title={menuItem.title}
                />
              );
            case 'sidebar-save-button':
              return (
                <SaveButton
                  {...commonProps}
                  label={isPublished ? (menuItem.isMVT ? 'Review and Publish' : 'Save and Publish') : 'Save'}
                  lastSavedTime={menuItem.componentProps.lastSavedTime}
                  onClick={() => menuItemClicked(menuItem)}
                  title={menuItem.title ?? ''}
                />
              );
            default:
              assertUnreachable(menuItem);
          }
        })}

        {visibleOverflowItems && visibleOverflowItems.length > 0 && (
          <Dropdown data-cy="overflow-menu">
            <Dropdown.Toggle className="pod-menu-item mb-2 w-100" id="overflow-menu" variant="sidebar">
              More options
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {map(visibleOverflowItems, (menuItem) => {
                const commonProps = getCommonProps(menuItem);

                if (menuItem.component === 'sidebar-button') {
                  return (
                    <Dropdown.Item
                      {...commonProps}
                      className={menuItem.cssClass}
                      data-cy={menuItem.id}
                      onClick={() => menuItemClicked(menuItem)}
                      title={menuItem.title}
                    >
                      {menuItem.label}
                    </Dropdown.Item>
                  );
                }
                return <Dropdown.Item>Invalid component</Dropdown.Item>;
              })}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </SidebarMenu>
    </SidebarContainer>
  );
};
